import React from 'react';
import {
  Box,
  VStack,
  Text,
  Heading,
  List,
  ListItem,
  Divider,
  Badge,
  Flex,
  useColorModeValue,
  Progress,
  HStack,
  IconButton
} from '@chakra-ui/react';
import {
  MapPin,
  FileText,
  Image,
  Clock,
  CheckCircle,
  CaretRight,
  CircleNotch
} from '@phosphor-icons/react';
import { useCreateSite } from '../../context/CreateSiteContext';
import { motion } from 'framer-motion';

// Motion components
const MotionBox = motion(Box);
const MotionListItem = motion(ListItem);
const MotionBadge = motion(Badge);

export const SiteCreationSidebar = () => {
  const {
    currentStep,
    steps,
    siteData,
    canProceed,
    handleNext,
    handlePrev
  } = useCreateSite();
  
  // Navigation item styling
  const bgColor = useColorModeValue('bg.surface', 'bg.surface');
  const activeBgColor = useColorModeValue('bg.accent', 'rgba(214, 173, 96, 0.1)');
  const activeTextColor = useColorModeValue('brand.primary', 'brand.primary');
  const inactiveTextColor = useColorModeValue('text.muted', 'text.muted');
  const navBgColor = useColorModeValue('bg.default', 'bg.default');
  const textColor = useColorModeValue('text.default', 'text.default');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const highlightColor = useColorModeValue('yellow.500', 'yellow.300');

  // Get step icon based on index
  const getStepIcon = (index) => {
    switch (index) {
      case 0: return FileText;
      case 1: return MapPin;
      case 2: return Clock;
      case 3: return Image;
      case 4: return CheckCircle;
      default: return FileText;
    }
  };

  // Get progress status for a step
  const getStepStatus = (index) => {
    if (currentStep === index) return 'active';
    if (currentStep > index) return 'completed';
    return 'pending';
  };

  // Check if step is accessible
  const canAccessStep = (index) => {
    if (index <= currentStep) return true;
    
    // Allow accessing future steps only if all previous steps are valid
    for (let i = 0; i < index; i++) {
      // Perform step-specific validation
      switch(i) {
        case 0:
          if (!siteData.name?.trim()) return false;
          break;
        case 1:
          if (!siteData.location?.coordinates?.lat || !siteData.location?.coordinates?.lng) return false;
          break;
        // Other steps can be accessed even if incomplete
      }
    }
    return true;
  };

  // Handle direct navigation to accessible steps
  const navigateToStep = (index) => {
    if (canAccessStep(index)) {
      // Calculate how many steps to move
      const stepDifference = index - currentStep;
      
      // Use handleNext or handlePrev multiple times to reach the target step
      if (stepDifference > 0) {
        // Need to go forward
        for (let i = 0; i < stepDifference; i++) {
          handleNext();
        }
      } else if (stepDifference < 0) {
        // Need to go backward
        for (let i = 0; i < Math.abs(stepDifference); i++) {
          handlePrev();
        }
      }
      // If stepDifference is 0, we're already at the target step
    }
  };

  return (
    <Box 
      bg={navBgColor} 
      py={6}
      px={4}
      position="relative"
      overflow="hidden"
      height="100%"
      borderRadius={{ base: 'md', md: '0' }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.06,
        pointerEvents: 'none',
        bgImage: 'var(--texture-aged)',
        bgRepeat: 'repeat',
        bgSize: '500px',
        zIndex: 0
      }}
    >
      {/* Header */}
      <Box 
        mb={6}
        position="relative"
        borderBottomWidth="1px"
        borderBottomColor={borderColor}
        pb={4}
      >
        <Heading
          as="h2"
          fontSize="2xl"
          fontFamily="heading"
          color={textColor}
          textAlign="center"
        >
          Create New Site
        </Heading>
        <Text 
          fontSize="sm" 
          color={inactiveTextColor} 
          mt={1}
          textAlign="center"
        >
          Navigation
        </Text>
      </Box>
      
      {/* Site name display */}
      {siteData.name && (
        <Box
          mb={6}
          px={4}
          py={3}
          bg={useColorModeValue('rgba(193, 148, 52, 0.1)', 'rgba(214, 173, 96, 0.1)')}
          borderRadius="md"
          position="relative"
          borderWidth="1px"
          borderColor={useColorModeValue('rgba(193, 148, 52, 0.3)', 'rgba(214, 173, 96, 0.3)')}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            pointerEvents: 'none',
            bgImage: 'var(--texture-parchment)',
            bgRepeat: 'repeat',
            bgSize: '200px',
            zIndex: 0
          }}
        >
          <Text
            fontFamily="heading" 
            fontSize="lg" 
            fontWeight="medium"
            color={activeTextColor}
            textAlign="center"
          >
            {siteData.name}
          </Text>
          
          {siteData.location?.address && (
            <Text fontSize="xs" color={inactiveTextColor} textAlign="center" mt={1}>
              {siteData.location.address}
            </Text>
          )}
        </Box>
      )}
      
      {/* Navigation Steps List */}
      <List spacing={3} position="relative" mb={8}>
        {steps.map((step, index) => {
          const StepIcon = getStepIcon(index);
          const status = getStepStatus(index);
          const isAccessible = canAccessStep(index);
          
          return (
            <React.Fragment key={index}>
              <MotionListItem
                display="flex"
                alignItems="center"
                px={3}
                py={3}
                borderRadius="md"
                cursor={isAccessible ? "pointer" : "not-allowed"}
                bg={status === 'active' ? activeBgColor : 'transparent'}
                onClick={() => isAccessible && navigateToStep(index)}
                whileHover={isAccessible ? { x: 4 } : {}}
                transition={{ duration: 0.2 }}
                position="relative"
                role="group"
                _before={{
                  content: '""',
                  position: 'absolute',
                  left: '-1rem',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bg: status === 'completed' ? activeTextColor : 'transparent'
                }}
                opacity={isAccessible ? 1 : 0.7}
              >
                {/* Step indicator icon */}
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  bg={status === 'active' ? 'brand.primary' : 'transparent'}
                  color={status === 'active' ? 'white' : status === 'completed' ? activeTextColor : inactiveTextColor}
                  boxSize="32px"
                  mr={3}
                  borderWidth="1px"
                  borderColor={status !== 'pending' ? 'brand.primary' : borderColor}
                >
                  {status === 'active' && (
                    <MotionBox
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                      position="absolute"
                    >
                      <CircleNotch size={24} />
                    </MotionBox>
                  )}
                  <StepIcon 
                    size={18} 
                    weight={status === 'completed' ? 'fill' : 'regular'} 
                    style={{ 
                      opacity: status === 'active' ? 1 : status === 'completed' ? 1 : 0.7,
                      visibility: status === 'active' ? 'hidden' : 'visible'
                    }}
                  />
                </Flex>
                
                {/* Step text */}
                <Box flex="1">
                  <Text 
                    fontSize="md" 
                    fontWeight={status !== 'pending' ? "medium" : "normal"}
                    color={status !== 'pending' ? textColor : inactiveTextColor}
                    fontFamily="heading"
                  >
                    {step.title}
                  </Text>
                  <Text 
                    fontSize="xs" 
                    color={inactiveTextColor}
                    fontFamily="body"
                  >
                    {step.description}
                  </Text>
                </Box>
                
                {/* Status indicators */}
                {status === 'active' && (
                  <MotionBadge 
                    colorScheme="yellow" 
                    variant="subtle" 
                    fontSize="2xs"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    Current
                  </MotionBadge>
                )}
                
                {status === 'completed' && (
                  <Box color={activeTextColor}>
                    <CheckCircle size={16} weight="fill" />
                  </Box>
                )}
                
                {status === 'pending' && isAccessible && (
                  <IconButton
                    icon={<CaretRight size={16} />}
                    aria-label="Go to step"
                    variant="ghost"
                    size="xs"
                    color={inactiveTextColor}
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    onClick={() => navigateToStep(index)}
                  />
                )}
              </MotionListItem>
              
              {/* Divider between steps */}
              {index < steps.length - 1 && (
                <Box pl={6} py={1}>
                  <Divider 
                    orientation="vertical" 
                    height="10px" 
                    borderStyle="dashed" 
                    borderColor={index < currentStep ? activeTextColor : borderColor}
                  />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </List>
      
      {/* Progress indicator */}
      <Box mt="auto" pt={4}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" color={inactiveTextColor}>Progress</Text>
          <Text fontSize="xs" fontWeight="bold" color={activeTextColor}>
            {Math.round((currentStep / (steps.length - 1)) * 100)}%
          </Text>
        </HStack>
        <Progress 
          value={(currentStep / (steps.length - 1)) * 100} 
          colorScheme="yellow" 
          size="sm" 
          borderRadius="full"
        />
        
        {/* Step count indicator */}
        <HStack mt={2} justify="space-between">
          <Text fontSize="xs" color={inactiveTextColor}>
            Step {currentStep + 1} of {steps.length}
          </Text>
          <Text fontSize="xs" color={highlightColor} fontWeight="medium">
            {steps[currentStep]?.title}
          </Text>
        </HStack>
      </Box>
    </Box>
  );
};